/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import Ajv, { ValidateFunction } from "ajv";
import { revivers } from "./schema-revivers";

const validators = new Map<string, ValidateFunction>();

const ajv = new Ajv({ strict: false });

ajv.addKeyword({
  keyword: "xreviver",
  post: true,
  modifying: true,
  validate: (keyword, value, _declaration, ctx) => {
    if (ctx === undefined) {
      throw new Error("ctx is undefined");
    }

    if (!(keyword in revivers)) {
      throw new Error(`Unknown reviver: ${keyword}`);
    }

    ctx.parentData[ctx.parentDataProperty] = revivers[keyword](value);
    return true;
  },
});

// messages.json is ~5MB and is only pulled in when the first response needs
// validating, so whichever API call lands first pays for its download + parse
// before its data reaches React Query. Memoized so concurrent callers share one
// load.
let messagesPromise: Promise<{ messages: any; pathsToMessage: any }> | undefined;

async function getMessages() {
  if (messagesPromise === undefined) {
    messagesPromise = import("./messages.json").then((loaded) => {
      const { messages, pathsToMessage } = loaded as any;

      return { messages, pathsToMessage };
    });
  }

  return messagesPromise;
}

async function getOrSetValidator(method: string, path: Path) {
  const fullPath = `${method} ${path}` as string;

  if (validators.has(fullPath)) {
    return validators.get(fullPath)!;
  }

  const { messages, pathsToMessage } = await getMessages();

  const messageName = pathsToMessage[fullPath];
  const messageSchema = (messages as any)[messageName];

  if (messageSchema) {
    const validator = ajv.compile(messageSchema);

    validators.set(fullPath, validator);

    return validator;
  }

  return;
}

type Path = string;

let warmStarted = false;

/**
 * Compiles validators for endpoints we already know are about to be requested,
 * before their responses arrive.
 *
 * Without this, `withValidator` pulls in the ~5MB messages.json module and runs
 * every `ajv.compile` in the gap between "response arrived" and "React Query has
 * data" — one unbroken ~320ms main-thread block on the critical path, because the
 * import is only triggered by the first response that needs validating.
 *
 * Scheduling, which took three attempts to get right — the failure mode each time
 * was the warm-up running *after* the responses it was meant to get ahead of:
 *
 * 1. `requestIdleCallback` never fired in time. During page load Chrome withholds
 *    idle time until well past the point the responses arrive.
 * 2. A chain of `setTimeout(…, 0)` between compiles was still too slow — timer
 *    callbacks queue behind the AngularJS bootstrap's own task stream, and the
 *    nesting clamp adds ~4ms a hop. The first compile landed ~700ms after the
 *    module was ready.
 * 3. `scheduler.postTask` at `user-visible` got the first compile in promptly but
 *    starved the rest — a 440ms gap before the second one, because user-visible
 *    yields to rendering and to the bootstrap's task stream.
 * 4. What works: `user-blocking` priority, plus time-slicing so a scheduler turn
 *    isn't spent on a 2ms compile. Small validators are batched into one slice;
 *    the expensive ones (first, by ordering) get a slice each.
 *
 * `user-blocking` is the honest priority here: this work sits directly between the
 * responses and the first render, so deprioritising it just moves the stall later.
 * The tradeoff is real — it competes with the AngularJS bootstrap, so the modal
 * mount can land marginally later in exchange for a much shorter post-response gap.
 *
 * Fire-and-forget: every failure is swallowed, since a miss only means the
 * validator gets compiled later on demand, exactly as it does today.
 */
type PostTaskScheduler = {
  postTask: (callback: () => void, options?: { priority?: string }) => Promise<unknown>;
};

function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    const maybeScheduler = (globalThis as { scheduler?: PostTaskScheduler }).scheduler;

    if (maybeScheduler !== undefined && typeof maybeScheduler.postTask === "function") {
      void maybeScheduler.postTask(() => resolve(), { priority: "user-blocking" });
      return;
    }

    setTimeout(resolve, 0);
  });
}

/**
 * How much compiling to do per scheduler turn. A slice ends up being this budget
 * plus however long the compile that overran it took, so keep it well under a
 * frame; the expensive validators are ordered first and each take a slice alone.
 */
const SLICE_BUDGET_MS = 25;

export function warmValidators(targets: Array<{ method: string; path: Path }>) {
  if (warmStarted || targets.length === 0) {
    return;
  }

  warmStarted = true;

  void (async () => {
    try {
      await getMessages();
    } catch {
      return;
    }

    let index = 0;

    while (index < targets.length) {
      // No yield before the first slice: the most expensive validator is first and
      // the whole point is to have it done before its response lands.
      if (index > 0) {
        await yieldToBrowser();
      }

      const sliceStartedAt = performance.now();

      do {
        const target = targets[index];
        index++;

        try {
          await getOrSetValidator(target.method, target.path);
        } catch {
          // fall through — it will be compiled on demand instead
        }
      } while (index < targets.length && performance.now() - sliceStartedAt < SLICE_BUDGET_MS);
    }
  })();
}

class InvalidResponseError extends Error {
  readonly detail: unknown;
  readonly responseJSON: unknown;
  readonly errorJSON: unknown;

  constructor(params: {
    message: string;
    detail: string;
    responseJSON: unknown;
    errorJSON: unknown;
  }) {
    super(params.message);
    this.detail = params.detail;
    this.responseJSON = params.responseJSON;
    this.errorJSON = params.errorJSON;
  }

  toJSON() {
    return {
      detail: this.detail,
      responseJSON: this.responseJSON,
      errorJSON: this.errorJSON,
    };
  }
}

async function getSocketMessageValidator(socketName: string) {
  const validatorKey = socketName;

  let validator = validators.get(validatorKey);
  if (validator) return validator;

  const { messages } = await getMessages();

  const message = (() => {
    const socketEvents = (messages as any)?.SocketEvents;

    if (!socketEvents) {
      return;
    }

    const { properties, definitions } = socketEvents;

    const reference = properties?.[socketName];

    if (reference === undefined) {
      return;
    }

    const messageName = reference.$ref.split("/").pop();

    const definition = definitions?.[messageName];

    if (definition === undefined) {
      return;
    }

    return {
      ...definition,
      definitions: definitions,
    };
  })();

  if (message) {
    validator = ajv.compile(message);
    validators.set(validatorKey, validator); // Cache the compiled validator
  }

  return validator;
}

export async function withValidator<T>(
  params:
    | {
        method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
        url: string;
        data: T;
      }
    | { socketName: string; data: T }
): Promise<T> {
  const validate =
    "method" in params
      ? await getOrSetValidator(params.method, params.url)
      : await getSocketMessageValidator(params.socketName);

  if (validate !== undefined) {
    const isValid = validate(params.data);

    if (isValid !== true) {
      throw new InvalidResponseError({
        message: `JSON schema validation error ${JSON.stringify(validate.errors)}`,
        detail: "",
        responseJSON: params.data,
        errorJSON: validate.errors,
      });
    }
  }

  return params.data;
}

export async function withSocketEventValidator<T extends object>(params: {
  validate: boolean;
  socketName: string;
  data: T;
}) {
  if (params.validate) {
    return withValidator({
      socketName: params.socketName,
      data: JSON.parse(JSON.stringify(params.data)),
    });
  }

  return params.data;
}
